import { useState, useMemo, useCallback } from 'react';
import {
    ShoppingCart, X, Trash2, CheckCircle2, AlertTriangle, RefreshCcw,
    ChevronRight, CheckSquare, Square, MinusSquare, Search, ChevronDown,
    Zap, Package, ArrowRight
} from 'lucide-react';
import { combineDescriptions } from '../lib/descriptions';

const PAGE_SIZE = 50; // Render only 50 at a time for DOM performance

/**
 * ScrapCart — Floating FAB + slide-in sidebar for batch operations.
 * Designed to handle 10k+ items with pagination and efficient rendering.
 */
export default function ScrapCart({ cartItems, allItems, onRemove, onClearCart, onApprove, onHold, onReScrap }) {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [processing, setProcessing] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

    // Resolve cart IDs to items (memoized for 10k)
    const cartData = useMemo(() => {
        const itemMap = new Map(allItems.map(i => [i.id, i]));
        return cartItems.map(id => itemMap.get(id)).filter(Boolean);
    }, [cartItems, allItems]);

    // Filter by search (memoized)
    const filteredData = useMemo(() => {
        if (!searchQuery.trim()) return cartData;
        const q = searchQuery.toLowerCase().trim();
        return cartData.filter(item =>
            (item.givenSupplierPartnumber || '').toLowerCase().includes(q) ||
            combineDescriptions(item).toLowerCase().includes(q) ||
            (item.givenSupplierName || '').toLowerCase().includes(q) ||
            (item.manufacturerName || '').toLowerCase().includes(q) ||
            (item.nounModifier || '').toLowerCase().includes(q) ||
            String(item.id).includes(q)
        );
    }, [cartData, searchQuery]);

    // Paginated visible slice (only render what's visible)
    const visibleData = useMemo(() => filteredData.slice(0, visibleCount), [filteredData, visibleCount]);
    const hasMore = visibleCount < filteredData.length;

    // Reset visible count when search changes
    const handleSearch = useCallback((val) => {
        setSearchQuery(val);
        setVisibleCount(PAGE_SIZE);
    }, []);

    const toggleSelect = useCallback((id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const toggleSelectAll = useCallback(() => {
        if (selectedIds.size === filteredData.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(filteredData.map(i => i.id)));
        }
    }, [filteredData, selectedIds.size]);

    const getSelectedArray = () => Array.from(selectedIds);

    const handleBulkAction = async (action) => {
        const ids = getSelectedArray();
        if (ids.length === 0) return;
        setProcessing(true);
        try {
            await action(ids);
            setSelectedIds(new Set());
        } finally {
            setProcessing(false);
        }
    };

    const handleReScrap = () => {
        const ids = selectedIds.size > 0 ? getSelectedArray() : cartItems;
        onReScrap(ids);
        setIsOpen(false);
    };

    // Stats
    const totalCount = cartItems.length;
    const selectedCount = selectedIds.size;

    return (
        <>
            {/* ── Floating FAB (always visible) ─────────────── */}
            {!isOpen && (
                <button
                    onClick={() => setIsOpen(true)}
                    className="fixed bottom-20 right-6 z-40 group"
                    title="Open Scrap Cart"
                >
                    <div className="relative w-14 h-14 rounded-2xl
                                    bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 text-white
                                    shadow-lg shadow-orange-500/25
                                    group-hover:shadow-xl group-hover:shadow-orange-500/35 group-hover:scale-105
                                    active:scale-95 transition-all duration-200
                                    flex items-center justify-center">
                        <ShoppingCart className="w-6 h-6" />
                        {totalCount > 0 && (
                            <span className="absolute -top-2 -right-2 min-w-[24px] h-[24px] px-1.5 rounded-full
                                             bg-white text-orange-600 text-[11px] font-black flex items-center justify-center
                                             shadow-md border-2 border-orange-100">
                                {totalCount > 999 ? `${(totalCount / 1000).toFixed(1)}k` : totalCount}
                            </span>
                        )}
                    </div>
                </button>
            )}

            {/* ── Backdrop ──────────────────────────────── */}
            {isOpen && (
                <div
                    className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-[3px] transition-opacity"
                    onClick={() => setIsOpen(false)}
                />
            )}

            {/* ── Sidebar Panel ──────────────────────────── */}
            <div className={`fixed top-0 right-0 z-50 h-full w-full max-w-[420px]
                            bg-white shadow-2xl border-l border-slate-200
                            transform transition-transform duration-300 ease-out
                            flex flex-col
                            ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>

                {/* ── Header ─────────────────────────────── */}
                <div className="shrink-0 border-b border-slate-200">
                    {/* Title row */}
                    <div className="px-5 pt-4 pb-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-sm">
                                <ShoppingCart className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h3 className="text-base font-extrabold text-slate-800">Scrap Cart</h3>
                                <p className="text-[11px] text-slate-500 font-medium">
                                    {totalCount.toLocaleString()} item{totalCount !== 1 ? 's' : ''}
                                    {selectedCount > 0 && <span className="text-primary-600"> · {selectedCount} selected</span>}
                                </p>
                            </div>
                        </div>
                        <button onClick={() => setIsOpen(false)}
                            className="p-2 rounded-xl hover:bg-slate-100 transition-colors">
                            <X className="w-5 h-5 text-slate-400" />
                        </button>
                    </div>

                    {/* Search */}
                    {totalCount > 5 && (
                        <div className="px-5 pb-3">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder={`Search ${totalCount.toLocaleString()} items…`}
                                    value={searchQuery}
                                    onChange={(e) => handleSearch(e.target.value)}
                                    className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl
                                               text-sm text-slate-700 placeholder-slate-400
                                               focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-400 transition-all"
                                />
                            </div>
                        </div>
                    )}

                    {/* Bulk action bar */}
                    {totalCount > 0 && (
                        <div className="px-5 pb-3 flex items-center gap-1.5 flex-wrap">
                            <button onClick={toggleSelectAll}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-bold
                                           bg-slate-100 text-slate-600 hover:bg-slate-200 transition-all border border-slate-200">
                                {selectedCount === filteredData.length && filteredData.length > 0
                                    ? <CheckSquare className="w-3.5 h-3.5 text-primary-600" />
                                    : selectedCount > 0
                                        ? <MinusSquare className="w-3.5 h-3.5" />
                                        : <Square className="w-3.5 h-3.5" />
                                }
                                {selectedCount > 0 ? `${selectedCount}` : 'All'}
                            </button>

                            {selectedCount > 0 && (
                                <>
                                    <button onClick={() => handleBulkAction(onApprove)} disabled={processing}
                                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-bold
                                                   bg-emerald-500 text-white hover:bg-emerald-600 transition-all disabled:opacity-50
                                                   shadow-sm shadow-emerald-500/20">
                                        <CheckCircle2 className="w-3 h-3" /> Approve
                                    </button>
                                    <button onClick={() => handleBulkAction(onHold)} disabled={processing}
                                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-bold
                                                   bg-orange-500 text-white hover:bg-orange-600 transition-all disabled:opacity-50
                                                   shadow-sm shadow-orange-500/20">
                                        <AlertTriangle className="w-3 h-3" /> Hold
                                    </button>
                                    <button onClick={() => {
                                        const ids = getSelectedArray();
                                        ids.forEach(id => onRemove(id));
                                        setSelectedIds(new Set());
                                    }} disabled={processing}
                                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-bold
                                                   bg-red-500 text-white hover:bg-red-600 transition-all disabled:opacity-50
                                                   shadow-sm shadow-red-500/20">
                                        <Trash2 className="w-3 h-3" />
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* ── Items list (paginated for 10k+ perf) ── */}
                <div className="flex-1 overflow-y-auto">
                    {cartData.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-center px-8">
                            <div className="w-20 h-20 rounded-3xl bg-slate-50 flex items-center justify-center mb-4">
                                <Package className="w-10 h-10 text-slate-200" />
                            </div>
                            <p className="text-sm font-semibold text-slate-400">Cart is empty</p>
                            <p className="text-xs text-slate-400 mt-1.5 max-w-[220px]">
                                Click the <ShoppingCart className="inline w-3.5 h-3.5 text-amber-500" /> icon on any row to add items
                            </p>
                        </div>
                    ) : filteredData.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-48 text-center px-8">
                            <Search className="w-8 h-8 text-slate-200 mb-2" />
                            <p className="text-sm text-slate-400">No items match "{searchQuery}"</p>
                        </div>
                    ) : (
                        <>
                            {visibleData.map((item, idx) => {
                                const isSelected = selectedIds.has(item.id);
                                return (
                                    <div key={item.id}
                                        className={`flex items-center gap-2.5 px-5 py-2.5 border-b border-slate-100
                                            hover:bg-slate-50/80 transition-colors cursor-default
                                            ${isSelected ? 'bg-primary-50/50' : ''}`}>
                                        {/* Select */}
                                        <button onClick={() => toggleSelect(item.id)} className="shrink-0">
                                            {isSelected
                                                ? <CheckSquare className="w-4 h-4 text-primary-600" />
                                                : <Square className="w-4 h-4 text-slate-300 hover:text-slate-500 transition-colors" />
                                            }
                                        </button>

                                        {/* Index */}
                                        <span className="text-[10px] text-slate-400 font-mono w-8 shrink-0 text-right">
                                            {idx + 1}
                                        </span>

                                        {/* Info */}
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs font-bold text-slate-700 truncate">
                                                {item.givenSupplierPartnumber || item.manufacturerNumber || `#${item.id}`}
                                            </p>
                                            <p className="text-[10px] text-slate-500 truncate">
                                                {combineDescriptions(item, ' / ') || '—'}
                                            </p>
                                        </div>

                                        {/* Noun/Modifier badge */}
                                        {item.nounModifier && (
                                            <span className="shrink-0 inline-flex px-1.5 py-0.5 rounded text-[9px] font-bold
                                                             bg-slate-100 text-slate-500 max-w-[80px] truncate">
                                                {item.nounModifier}
                                            </span>
                                        )}

                                        {/* Remove */}
                                        <button onClick={() => onRemove(item.id)}
                                            className="shrink-0 p-1 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-all">
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                );
                            })}

                            {/* Load More */}
                            {hasMore && (
                                <button
                                    onClick={() => setVisibleCount(prev => prev + PAGE_SIZE)}
                                    className="w-full py-3 text-center text-xs font-semibold text-primary-600
                                               hover:bg-primary-50/50 transition-colors flex items-center justify-center gap-1.5"
                                >
                                    <ChevronDown className="w-4 h-4" />
                                    Show more ({Math.min(PAGE_SIZE, filteredData.length - visibleCount)} of {(filteredData.length - visibleCount).toLocaleString()} remaining)
                                </button>
                            )}
                        </>
                    )}
                </div>

                {/* ── Footer ──────────────────────────────── */}
                {cartData.length > 0 && (
                    <div className="shrink-0 border-t border-slate-200 bg-gradient-to-t from-slate-50 to-white p-4 space-y-2">
                        {/* Re-Scrap All — primary CTA */}
                        <button
                            onClick={handleReScrap}
                            disabled={processing}
                            className="w-full inline-flex items-center justify-center gap-2.5 px-4 py-3 rounded-xl text-sm font-extrabold
                                       bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 text-white
                                       hover:from-indigo-700 hover:via-purple-700 hover:to-indigo-700
                                       shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/35
                                       active:scale-[0.98] transition-all duration-200 disabled:opacity-50"
                        >
                            <Zap className="w-4.5 h-4.5" />
                            Re-Scrap {selectedCount > 0 ? `Selected (${selectedCount.toLocaleString()})` : `All (${totalCount.toLocaleString()})`}
                            <ArrowRight className="w-4 h-4 ml-1" />
                        </button>

                        {/* Clear */}
                        <button
                            onClick={onClearCart}
                            className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold
                                       text-slate-400 hover:text-red-600 hover:bg-red-50 transition-all"
                        >
                            <Trash2 className="w-3.5 h-3.5" /> Clear Cart
                        </button>
                    </div>
                )}
            </div>
        </>
    );
}
